import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import AccessPage from "../Shared/accessPage";

const RedTeamingSettings = () => {
  const [settings, setSettings] = useState({
    // Asset Exclusions
    excludedAssets: [],
    excludedIPs: [],
    excludedDomains: [],
    
    // Default Settings
    defaultFrequency: "weekly",
    defaultAttackTypes: ["port_scan", "ssh_brute"],
    maxConcurrentSimulations: 5,
    
    // Notification Settings
    emailNotifications: true,
    slackNotifications: false,
    notificationRecipients: [],
    
    // Security Settings
    requireApproval: false,
    maxSimulationDuration: 30, // minutes
    rateLimitPerHour: 10,
    
    // Reporting Settings
    autoGenerateReports: true,
    reportRetentionDays: 90,
    includeRemediationSuggestions: true
  });

  const [availableAssets] = useState([
    { id: 1, name: "api.domain.com", type: "API Server", ip: "192.168.1.10" },
    { id: 2, name: "dev.site.io", type: "Development Server", ip: "192.168.1.11" },
    { id: 3, name: "staging.example.com", type: "Staging Server", ip: "192.168.1.12" },
    { id: 4, name: "login.company.com", type: "Authentication Server", ip: "192.168.1.13" },
    { id: 5, name: "admin.portal.net", type: "Admin Portal", ip: "192.168.1.14" }
  ]);

  const [availableAttackTypes] = useState([
    { id: "port_scan", name: "Port Scanning", description: "Simulate port scanning to identify open services" },
    { id: "ssh_brute", name: "SSH Brute Force", description: "Test SSH authentication security" },
    { id: "web_exploit", name: "Web Exploit (CVE Simulation)", description: "Simulate known web vulnerabilities" },
    { id: "misconfig", name: "Misconfig Detection", description: "Identify security misconfigurations" },
    { id: "subdomain_takeover", name: "Subdomain Takeover", description: "Test DNS and subdomain security" },
    { id: "vulnerability_probe", name: "Vulnerability Probing", description: "Probe for common vulnerabilities" }
  ]);

  const vendorId = sessionStorage.getItem('vendorId');
  const vendorName = sessionStorage.getItem('vendorName');

  useEffect(() => {
    // Load settings from API/localStorage
    const savedSettings = localStorage.getItem('redTeamingSettings');
    if (savedSettings) {
      setSettings(JSON.parse(savedSettings));
    }
  }, []);

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleAssetExclusion = (assetId, action) => {
    if (action === 'add') {
      setSettings(prev => ({
        ...prev,
        excludedAssets: [...prev.excludedAssets, assetId]
      }));
    } else {
      setSettings(prev => ({
        ...prev,
        excludedAssets: prev.excludedAssets.filter(id => id !== assetId)
      }));
    }
  };

  const handleAttackTypeToggle = (attackTypeId) => {
    setSettings(prev => ({
      ...prev,
      defaultAttackTypes: prev.defaultAttackTypes.includes(attackTypeId)
        ? prev.defaultAttackTypes.filter(id => id !== attackTypeId)
        : [...prev.defaultAttackTypes, attackTypeId]
    }));
  };

  const handleSaveSettings = () => {
    // Save to localStorage and API
    localStorage.setItem('redTeamingSettings', JSON.stringify(settings));
    toastr.success("Success", "Red Teaming settings saved successfully!");
  };

  const handleResetSettings = () => {
    const defaultSettings = {
      excludedAssets: [],
      excludedIPs: [],
      excludedDomains: [],
      defaultFrequency: "weekly",
      defaultAttackTypes: ["port_scan", "ssh_brute"],
      maxConcurrentSimulations: 5,
      emailNotifications: true,
      slackNotifications: false,
      notificationRecipients: [],
      requireApproval: false,
      maxSimulationDuration: 30,
      rateLimitPerHour: 10,
      autoGenerateReports: true,
      reportRetentionDays: 90,
      includeRemediationSuggestions: true
    };
    setSettings(defaultSettings);
    toastr.info("Info", "Settings reset to defaults");
  };

  return (
    <>
      <div className="container-fluid">
        <AccessPage></AccessPage>
        <div className="block-header">
          <div className="row clearfix">
            <div className="col-md-6 col-sm-12">
              <h1 className="welcome-text">Red Teaming Settings</h1>
              <nav aria-label="breadcrumb" className="mt-1">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    {vendorId ? (
                      <Link to="/vendor" onClick={() => sessionStorage.clear()}>
                        Vendor Assessment
                      </Link>
                    ) : (
                      <Link to="/" className="welcome-textSmall">Home</Link>
                    )}
                  </li>
                  {vendorName && (
                    <li className="breadcrumb-item active" aria-current="page" style={{ color: "white" }}>
                      <Link to={`/dashboard/vendor/${vendorId}`}>{vendorName}</Link>
                    </li>
                  )}
                  <li className="breadcrumb-item">
                    <Link to="/red-teaming" className="welcome-textSmall">Red Teaming</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page" style={{ color: "white" }}>
                    Settings
                  </li>
                </ol>
              </nav>
            </div>
            <div className="col-md-6 col-sm-12 hidden-xs d-flex justify-content-end align-items-center">
              <button
                onClick={handleResetSettings}
                className="btn btn-outline-secondary mr-2"
              >
                Reset to Defaults
              </button>
              <button
                onClick={handleSaveSettings}
                className="btn btn-primary"
              >
                Save Settings
              </button>
            </div>
          </div>
        </div>

        <div className="row clearfix">
          {/* Asset Exclusions */}
          <div className="col-lg-6 col-md-12">
            <div className="card">
              <div className="header">
                <h2>Asset Exclusions</h2>
                <small>Configure assets that should be excluded from red team simulations</small>
              </div>
              <div className="body">
                <h6>Exclude Assets</h6>
                <div className="asset-exclusion-list">
                  {availableAssets.map((asset) => (
                    <div key={asset.id} className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`exclude-asset-${asset.id}`}
                        checked={settings.excludedAssets.includes(asset.id)}
                        onChange={(e) => handleAssetExclusion(asset.id, e.target.checked ? 'add' : 'remove')}
                      />
                      <label className="form-check-label" htmlFor={`exclude-asset-${asset.id}`}>
                        <strong>{asset.name}</strong> ({asset.ip}) - {asset.type}
                      </label>
                    </div>
                  ))}
                </div>

                <div className="mt-4">
                  <h6>Exclude IP Ranges</h6>
                  <div className="input-group mb-3">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter IP range (e.g., 192.168.1.0/24)"
                      value={settings.excludedIPs.join(', ')}
                      onChange={(e) => handleSettingChange('excludedIPs', e.target.value.split(',').map(ip => ip.trim()))}
                    />
                    <div className="input-group-append">
                      <span className="input-group-text">IP Ranges</span>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <h6>Exclude Domains</h6>
                  <div className="input-group mb-3">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter domains (comma-separated)"
                      value={settings.excludedDomains.join(', ')}
                      onChange={(e) => handleSettingChange('excludedDomains', e.target.value.split(',').map(domain => domain.trim()))}
                    />
                    <div className="input-group-append">
                      <span className="input-group-text">Domains</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Default Settings */}
          <div className="col-lg-6 col-md-12">
            <div className="card">
              <div className="header">
                <h2>Default Settings</h2>
                <small>Configure default behavior for new simulations</small>
              </div>
              <div className="body">
                <div className="form-group">
                  <label>Default Frequency</label>
                  <select
                    className="form-control"
                    value={settings.defaultFrequency}
                    onChange={(e) => handleSettingChange('defaultFrequency', e.target.value)}
                  >
                    <option value="once">Run Once</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Default Attack Types</label>
                  <div className="attack-types-list">
                    {availableAttackTypes.map((attackType) => (
                      <div key={attackType.id} className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`default-attack-${attackType.id}`}
                          checked={settings.defaultAttackTypes.includes(attackType.id)}
                          onChange={() => handleAttackTypeToggle(attackType.id)}
                        />
                        <label className="form-check-label" htmlFor={`default-attack-${attackType.id}`}>
                          <strong>{attackType.name}</strong>
                          <br />
                          <small className="text-muted">{attackType.description}</small>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Max Concurrent Simulations</label>
                  <input
                    type="number"
                    className="form-control"
                    value={settings.maxConcurrentSimulations}
                    onChange={(e) => handleSettingChange('maxConcurrentSimulations', parseInt(e.target.value))}
                    min="1"
                    max="20"
                  />
                </div>

                <div className="form-group">
                  <label>Max Simulation Duration (minutes)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={settings.maxSimulationDuration}
                    onChange={(e) => handleSettingChange('maxSimulationDuration', parseInt(e.target.value))}
                    min="5"
                    max="120"
                  />
                </div>

                <div className="form-group">
                  <label>Rate Limit (simulations per hour)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={settings.rateLimitPerHour}
                    onChange={(e) => handleSettingChange('rateLimitPerHour', parseInt(e.target.value))}
                    min="1"
                    max="100"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row clearfix">
          {/* Notification Settings */}
          <div className="col-lg-6 col-md-12">
            <div className="card">
              <div className="header">
                <h2>Notification Settings</h2>
                <small>Configure how you receive simulation notifications</small>
              </div>
              <div className="body">
                <div className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="emailNotifications"
                    checked={settings.emailNotifications}
                    onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="emailNotifications">
                    Email Notifications
                  </label>
                </div>

                <div className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="slackNotifications"
                    checked={settings.slackNotifications}
                    onChange={(e) => handleSettingChange('slackNotifications', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="slackNotifications">
                    Slack Notifications
                  </label>
                </div>

                <div className="form-group">
                  <label>Notification Recipients (comma-separated emails)</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="user1@company.com, user2@company.com"
                    value={settings.notificationRecipients.join(', ')}
                    onChange={(e) => handleSettingChange('notificationRecipients', e.target.value.split(',').map(email => email.trim()))}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Security & Reporting Settings */}
          <div className="col-lg-6 col-md-12">
            <div className="card">
              <div className="header">
                <h2>Security & Reporting</h2>
                <small>Configure security controls and reporting preferences</small>
              </div>
              <div className="body">
                <div className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="requireApproval"
                    checked={settings.requireApproval}
                    onChange={(e) => handleSettingChange('requireApproval', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="requireApproval">
                    Require Approval for Simulations
                  </label>
                </div>

                <div className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="autoGenerateReports"
                    checked={settings.autoGenerateReports}
                    onChange={(e) => handleSettingChange('autoGenerateReports', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="autoGenerateReports">
                    Auto-generate Reports
                  </label>
                </div>

                <div className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="includeRemediationSuggestions"
                    checked={settings.includeRemediationSuggestions}
                    onChange={(e) => handleSettingChange('includeRemediationSuggestions', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="includeRemediationSuggestions">
                    Include Remediation Suggestions
                  </label>
                </div>

                <div className="form-group">
                  <label>Report Retention (days)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={settings.reportRetentionDays}
                    onChange={(e) => handleSettingChange('reportRetentionDays', parseInt(e.target.value))}
                    min="30"
                    max="365"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Save Actions */}
        <div className="row clearfix">
          <div className="col-12">
            <div className="card">
              <div className="body text-center">
                <button
                  onClick={handleSaveSettings}
                  className="btn btn-primary btn-lg mr-3"
                >
                  <i className="fa fa-save mr-2"></i>
                  Save Settings
                </button>
                <button
                  onClick={handleResetSettings}
                  className="btn btn-outline-secondary btn-lg"
                >
                  <i className="fa fa-refresh mr-2"></i>
                  Reset to Defaults
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RedTeamingSettings; 